import DOMPurify from 'dompurify';

const TEXT_AND_HEADLINE_TAGS = [
  'p',
  'div',
  'span',
  'label',
  'figcaption',
  'caption',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const;

const BLOCK_LEVEL_TAGS = [
  'address',
  'article',
  'aside',
  'blockquote',
  'div',
  'dl',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'thead',
  'tfoot',
  'tr',
  'td',
  'th',
  'ul',
] as const;

export const hasHtmlWrapper = (value: string): boolean => {
  const trimmedValue = value.trim();

  if (!trimmedValue || typeof document === 'undefined') {
    return false;
  }

  const root = document.createElement('div');
  root.innerHTML = DOMPurify.sanitize(trimmedValue);

  if (root.children.length !== 1) {
    return false;
  }

  const [wrapperElement] = Array.from(root.children);
  const wrapperTag = wrapperElement.tagName.toLowerCase();

  if (!TEXT_AND_HEADLINE_TAGS.includes(wrapperTag as (typeof TEXT_AND_HEADLINE_TAGS)[number])) {
    return false;
  }

  return wrapperElement.outerHTML.trim() === trimmedValue;
};

export const decodeHtmlEntities = (value: string): string => {
  if (typeof document === 'undefined') {
    return value;
  }

  const textArea = document.createElement('textarea');
  textArea.innerHTML = DOMPurify.sanitize(value);

  return textArea.value;
};

export const normalizeImprintText = (value: string): string => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  if (hasHtmlWrapper(trimmedValue)) {
    return trimmedValue;
  }

  const blockTagsPattern = BLOCK_LEVEL_TAGS.join('|');
  if (new RegExp(`<(${blockTagsPattern})\\b`, 'i').test(trimmedValue)) {
    return trimmedValue;
  }

  return `<p>${trimmedValue}</p>`;
};

export const sanitizeImprintText = (value: string): string => {
  const decodedImprint = decodeHtmlEntities(value);
  const normalizedImprint = normalizeImprintText(decodedImprint);

  return DOMPurify.sanitize(normalizedImprint);
};
